import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import EmailPreviewModal from '@/modules/admin-dashboard/components/modals/EmailPreviewModal.tsx';
import {
  useBulkSendRemindersMutation,
  useTaskReminderOptionsQuery,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { BaseModalProps, Button, CustomCheckbox, ModalContentWrapper, Spinner } from 'ui';
import { copyTextToClipBoard } from 'ui/utils';
import {
  ReminderOptionResponse,
  ReminderSendResult,
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
} from 'compliance-api';

interface RemindAssigneeModalProps extends BaseModalProps {
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse;
}

const REMINDER_REASON_LABELS: Record<string, string> = {
  duplicate: 'Email already sent. Only one email can be sent every 24 hours',
  already_sent: 'Email already sent. Only one email can be sent every 24 hours',
  already_completed: 'Already completed',
  no_longer_assigned: 'No longer assigned',
  preview_stale: 'Preview changed',
  invalid_notification_id: 'Reminder is no longer valid',
  invalid_occurrence: 'Task occurrence is invalid',
  occurrence_changed: 'Task schedule changed. Please refresh reminders',
  occurrence_not_overdue: 'Task occurrence is not overdue',
  occurrence_already_overdue: 'Task occurrence is now overdue. Please refresh reminders',
  mail_provider_error: 'Email delivery failed',
  idempotency_persistence_failed: 'Email was sent, but delivery status could not be saved',
};

const getResultLine = (
  result: ReminderSendResult,
  users: { user_id: number; first_name: string; last_name: string }[],
) => {
  const resultName = [result.first_name, result.last_name].filter(Boolean).join(' ');
  const optionUser = users.find(user => user.user_id === result.user_id);
  const userName =
    resultName || `${optionUser?.first_name || ''} ${optionUser?.last_name || ''}`.trim();
  const reason = result.reason
    ? REMINDER_REASON_LABELS[result.reason] || result.reason
    : 'Email sent';

  return `${userName || `User #${result.user_id}`} — ${reason}`;
};

const RemindAssigneeModal = ({ task, open, onClose }: RemindAssigneeModalProps) => {
  const [selectedNotificationIds, setSelectedNotificationIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<ReminderOptionResponse>();
  const [sendResult, setSendResult] = useState<{
    sent: ReminderSendResult[];
    skipped: ReminderSendResult[];
    failed: ReminderSendResult[];
  }>();
  const reminderOptionsQuery = useTaskReminderOptionsQuery(task.id, !open);
  const { mutateAsync: sendReminders, isLoading: isSendingReminders } =
    useBulkSendRemindersMutation();

  useEffect(() => {
    if (!open) {
      setSelectedNotificationIds([]);
      setPreview(undefined);
      setSendResult(undefined);
    }
  }, [open]);

  useEffect(() => {
    const validNotificationIds = new Set(
      (reminderOptionsQuery.data?.users ?? [])
        .flatMap(user => user.notifications)
        .map(notification => notification.notification_id),
    );

    setSelectedNotificationIds(current => {
      const next = current.filter(id => validNotificationIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [reminderOptionsQuery.data]);

  const handleToggleSelection = useCallback((notificationId: string, checked: boolean) => {
    setSelectedNotificationIds(ids =>
      checked ? [...ids, notificationId] : ids.filter(id => id !== notificationId),
    );
  }, []);

  const handleSendReminderEmails = useCallback(async () => {
    const notifications = (reminderOptionsQuery.data?.users ?? [])
      .flatMap(user => user.notifications)
      .filter(notification => selectedNotificationIds.includes(notification.notification_id))
      .map(({ notification_id, content_hash }) => ({ notification_id, content_hash }));

    if (!notifications.length) {
      setSelectedNotificationIds([]);
      toast.error('Selected reminders are no longer available');
      return;
    }

    const toastId = toast.loading('Sending emails...');

    try {
      const result = await sendReminders({ task_id: task.id, notifications });
      setSendResult({
        sent: result.sent ?? [],
        skipped: result.skipped ?? [],
        failed: result.failed ?? [],
      });
      setSelectedNotificationIds([]);
      setPreview(undefined);
      await reminderOptionsQuery.refetch();
      const hasSentWarnings = result.sent?.some(item => !!item.reason);
      if (!result.skipped?.length && !result.failed?.length && !hasSentWarnings) {
        toast.success('Emails sent', { id: toastId });
        onClose();
      } else {
        toast.dismiss(toastId);
      }
    } catch {
      // Keep the selection intact: the request may have reached the server.
      toast.error('Failed to send emails', { id: toastId });
    }
  }, [onClose, reminderOptionsQuery, selectedNotificationIds, sendReminders, task.id]);

  const handleCopyLink = useCallback((publicLink: string) => {
    copyTextToClipBoard(publicLink, { successMessage: 'Authorisation link copied' });
  }, []);

  return (
    <>
      <ModalContentWrapper
        title="Remind assignee"
        open={open && !preview}
        onClose={onClose}
        classes={{ dialogPanel: '!max-w-5xl' }}
      >
        <div className="mb-4">
          <div className="text-sm text-gray-500">Task</div>
          <div className="font-medium break-words">{task.name}</div>
        </div>

        {reminderOptionsQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : reminderOptionsQuery.isError ? (
          <div className="text-sm text-red-600">
            Unable to load reminder options. Please try again.
          </div>
        ) : (
          <div className="mt-6 border-y border-gray-200">
            {reminderOptionsQuery.data?.users.flatMap(user => {
              const userName = `${user.first_name} ${user.last_name}`;

              if (!user.notifications.length) {
                const message =
                  user.status === 'completed'
                    ? 'Completed — no reminders available'
                    : 'No reminders available';

                return (
                  <div
                    key={user.user_id}
                    className="flex items-center gap-4 py-[14px] border-b border-gray-200 last:border-b-0"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <CustomCheckbox disabled />
                      <div className="truncate font-medium">{userName}</div>
                      <div className="text-sm text-gray-500 shrink-0">({message})</div>
                    </div>
                  </div>
                );
              }

              return user.notifications.map(notification => {
                const checked = selectedNotificationIds.includes(notification.notification_id);
                return (
                  <div key={notification.notification_id}>
                    <div className="flex items-center gap-4 py-[14px] border-b border-gray-200">
                      <div className="flex items-center gap-4 min-w-0">
                        <CustomCheckbox
                          value={notification.notification_id}
                          checked={checked}
                          disabled={isSendingReminders}
                          onChange={handleToggleSelection.bind(
                            null,
                            notification.notification_id,
                            !checked,
                          )}
                        />
                        <div className="truncate font-medium">{userName}</div>
                        <div className="shrink-0 text-sm text-gray-500">({notification.label})</div>
                      </div>
                      <div className="flex shrink-0 gap-3 text-sm ml-auto">
                        <button
                          type="button"
                          className="text-blue-700 hover:underline"
                          onClick={() => setPreview(notification)}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="text-blue-700 hover:underline"
                          onClick={() => handleCopyLink(notification.public_link)}
                        >
                          Copy link
                        </button>
                      </div>
                    </div>
                  </div>
                );
              });
            })}
          </div>
        )}

        {sendResult && (
          <div className="mt-4 space-y-2 text-sm">
            {!!sendResult.sent.length && (
              <div className="text-green-700">
                <div className="font-medium">Sent</div>
                <ul className="mt-1 space-y-1">
                  {sendResult.sent.map(result => (
                    <li key={`${result.request_index}-${result.notification_id}`}>
                      {getResultLine(result, reminderOptionsQuery.data?.users ?? [])}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!sendResult.skipped.length && (
              <div className="text-amber-700">
                <div className="font-medium">Skipped</div>
                <ul className="mt-1 space-y-1">
                  {sendResult.skipped.map(result => (
                    <li key={`${result.request_index}-${result.notification_id}`}>
                      {getResultLine(result, reminderOptionsQuery.data?.users ?? [])}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!sendResult.failed.length && (
              <div className="text-red-600">
                <div className="font-medium">Failed</div>
                <ul className="mt-1 space-y-1">
                  {sendResult.failed.map(result => (
                    <li key={`${result.request_index}-${result.notification_id}`}>
                      {getResultLine(result, reminderOptionsQuery.data?.users ?? [])}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <Button
          className="mt-4"
          disabled={
            !selectedNotificationIds.length || isSendingReminders || reminderOptionsQuery.isLoading
          }
          loading={isSendingReminders}
          onClick={handleSendReminderEmails}
        >
          Send emails
        </Button>
      </ModalContentWrapper>
      <EmailPreviewModal
        open={!!preview}
        onClose={() => setPreview(undefined)}
        subject={preview?.subject || ''}
        html={preview?.body_html || ''}
        previewKey={preview?.notification_id}
      />
    </>
  );
};

export default RemindAssigneeModal;
